import { Agent, Policy, SafetyEvent, Incident, DashboardStats, AuthSession, ApplicationIntegration, RetentionPolicy, AgentRun, TraceEvent, AgentPosture, AuditLogEntry, TenantWorkspace, PlatformMetrics, OrganizationMember } from '../types';
import type { ValidationReport } from '../server/engine/validator';
import type { RiskAnalysisResponse } from '../server/engine/risk';

const API_BASE = '/api';

export class ApiResponseError extends Error {
  constructor(message: string, readonly status: number) { super(message); }
}

export type ValidationRunReport=ValidationReport & {run_id:string;release_decision:'pending'|'approved'|'rejected';agent_status?:string};

export interface ValidationHistoryRecord {
  id: string;
  agent_id: string;
  agent_name?: string;
  version: string;
  status: 'passed' | 'flagged' | 'failed';
  total_tests: number;
  passed_tests: number;
  failed_tests: number;
  flagged_tests?: number;
  safety_score: number;
  recommendation: string;
  release_decision?: 'pending' | 'approved' | 'rejected';
  release_note?: string | null;
  released_by?: string | null;
  released_at?: string | null;
  details_json: Record<string, unknown>;
  created_at: string;
}

export interface ProfileVersionRecord {
  id: string;
  agent_id: string;
  version: string;
  snapshot_json: Record<string, unknown>;
  changed_by: string;
  change_reason?: string | null;
  created_at: string;
}

export interface GatewayEvaluationResponse {
  decision: 'ALLOW' | 'REVIEW' | 'BLOCK';
  risk_score?: number;
  reasons?: string[];
  mitigation: string;
  demo?: boolean;
  event_id?: string;
  incident_id?: string;
}

async function requestJson<T>(path: string, init?: RequestInit): Promise<T> {
  const activeTenant = typeof window !== 'undefined' ? localStorage.getItem('agenticscale_active_tenant') : null;
  const response = await fetch(`${API_BASE}${path}`, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(activeTenant ? { 'x-tenant-id': activeTenant } : {}),
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers || {})
    }
  });

  const raw = await response.text();
  let payload: unknown = null;
  try {
    payload = raw ? JSON.parse(raw) : null;
  } catch {
    const dataLine = raw.split(/\r?\n/).find((line) => line.startsWith('data:'));
    if (dataLine) {
      try {
        payload = JSON.parse(dataLine.slice(5).trim());
      } catch {
        payload = null;
      }
    }
  }

  if (!response.ok) {
    const serverMessage = payload && typeof payload === 'object' && 'error' in payload
      ? String((payload as { error: unknown }).error)
      : `Request failed with status ${response.status}.`;
    const message = response.status === 401 && /AUTH_REQUIRED|signed-in|organization login/i.test(serverMessage)
      ? 'Sign in through your organization login to continue.'
      : serverMessage;
    throw new ApiResponseError(message, response.status);
  }

  return payload as T;
}

export const api = {
  getSession: () => requestJson<AuthSession>('/auth/session'),
  getHealth: () => requestJson<{ status: string; database: { status: string }; capabilities?: { admin_auth_configured?: boolean; gateway_auth_configured?: boolean; organization_auth_configured?: boolean; demo_mode_available?: boolean; provider?: string } }>('/health'),

  reviewAgent: (description: string, agentName?: string, owner?: string) => requestJson<RiskAnalysisResponse>('/review', {
    method: 'POST',
    body: JSON.stringify({ description, agent_name: agentName, owner })
  }),

  verifyAdminKey: () => requestJson<{ authenticated: boolean; message: string }>('/auth/admin', {
    method: 'POST',
    body: JSON.stringify({})
  }),

  getAgents: (options?: { includeArchived?: boolean }) => requestJson<Agent[]>(`/agents${options?.includeArchived ? '?include_archived=true' : ''}`),

  saveAgent: (agentData: Partial<Agent>) => requestJson<{ success: boolean; id: string }>('/agents', {
    method: 'POST',
    body: JSON.stringify(agentData)
  }),

  getProfileHistory: (agentId: string) => requestJson<ProfileVersionRecord[]>(`/agents/${encodeURIComponent(agentId)}/history`),

  rollbackAgent: (agentId: string, versionId: string, changeReason?: string) => requestJson<{ success: boolean; agent: Agent; message: string }>(`/agents/${encodeURIComponent(agentId)}/rollback`, {
    method: 'POST',
    body: JSON.stringify({ version_id: versionId, change_reason: changeReason })
  }),

  getAuditLog: (limit = 25) => requestJson<AuditLogEntry[]>(`/audit-log?limit=${encodeURIComponent(String(limit))}`),

  archiveAgent: (agentId: string) => requestJson<{ success: boolean }>(`/agents/${encodeURIComponent(agentId)}/archive`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  restoreAgent: (agentId: string) => requestJson<{ success: boolean }>(`/agents/${encodeURIComponent(agentId)}/restore`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  getPolicies: () => requestJson<Policy[]>('/policies'),

  updatePolicy: (policyId: string, enabled: boolean) => requestJson<{ success: boolean; id: string; is_enabled: boolean }>(`/policies/${encodeURIComponent(policyId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ is_enabled: enabled })
  }),

  validateAgent: (agentId: string, agentName: string, version: string) => requestJson<ValidationRunReport>('/validate', {
    method: 'POST',
    body: JSON.stringify({ agent_id: agentId, agent_name: agentName, version })
  }),

  releaseValidation: (runId: string, decision: 'approve' | 'reject', note?: string) => requestJson<{ success: boolean; release_decision: 'approved' | 'rejected'; agent_status?: string; message: string }>(`/validate/${encodeURIComponent(runId)}/release`, {
    method: 'POST',
    body: JSON.stringify({ decision, note })
  }),

  getValidationHistory: () => requestJson<ValidationHistoryRecord[]>('/validate/history'),

  evaluateGateway: (payload: {
    agent_id: string;
    action_name: string;
    target_resource: string;
    payload?: Record<string, unknown>;
    prompt_input?: string;
    summary?: string;
    application_id?: string;
    trace_id?: string;
    run_id?: string;
    tool_name?: string;
    model?: string;
    environment?: string;
    metadata?: Record<string, unknown>;
  }) => requestJson<GatewayEvaluationResponse>('/gateway/evaluate', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  getDashboardStats: () => requestJson<DashboardStats>('/dashboard/stats'),

  getEvents: (params?: { decision?: string; agent_id?: string; limit?: number }) => {
    const query = new URLSearchParams();
    if (params?.decision) query.set('decision', params.decision);
    if (params?.agent_id) query.set('agent_id', params.agent_id);
    if (params?.limit) query.set('limit', String(params.limit));
    return requestJson<SafetyEvent[]>(`/events?${query.toString()}`);
  },

  getIncidents: () => requestJson<Incident[]>('/incidents'),

  getIntegrations: () => requestJson<ApplicationIntegration[]>('/integrations/apps'),

  createIntegration: (payload: { name: string; description?: string; environment?: string; agent_id?: string }) => requestJson<{ application: ApplicationIntegration; integration_key: string; integration_key_warning: string; scopes: string[] }>('/integrations/apps', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  rotateIntegration: (applicationId: string) => requestJson<{ application_id: string; integration_key: string; integration_key_warning: string; scopes: string[] }>(`/integrations/apps/${encodeURIComponent(applicationId)}/rotate`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  revokeIntegration: (applicationId: string) => requestJson<{ success: boolean }>(`/integrations/apps/${encodeURIComponent(applicationId)}/revoke`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  getRetention: () => requestJson<RetentionPolicy>('/retention'),

  updateRetention: (payload: { hot_retention_days: number; archive_enabled: boolean; legal_hold: boolean }) => requestJson<RetentionPolicy>('/retention', {
    method: 'PATCH',
    body: JSON.stringify(payload)
  }),

  pruneRetention: () => requestJson<{ success: boolean; deleted: number; cutoff_days: number }>('/retention/prune', {
    method: 'POST',
    body: JSON.stringify({})
  }),

  archiveToR2: () => requestJson<{
    success: boolean;
    export_id: string;
    storage_key: string;
    record_count: number;
    integrity_hash: string;
    r2_bound: boolean;
    archived_at: string;
  }>('/retention/archive-to-r2', {
    method: 'POST',
    body: JSON.stringify({})
  }),

  getArchives: () => requestJson<Array<{
    id: string;
    organization_id: string;
    format: string;
    record_count: number;
    integrity_hash: string;
    created_at: string;
  }>>('/retention/archives'),

  getRuns: (signal?: AbortSignal) => requestJson<AgentRun[]>('/observability/runs', { signal }),

  getRunEvidence: (runId: string, signal?: AbortSignal) => requestJson<{ run: AgentRun; events: TraceEvent[] }>(`/observability/runs/${encodeURIComponent(runId)}`, { signal }),

  getPosture: (signal?: AbortSignal) => requestJson<AgentPosture[]>('/observability/posture', { signal }),

  exportEvidence: (params?: { agent_id?: string; run_id?: string }, signal?: AbortSignal) => {
    const query = new URLSearchParams();
    if (params?.agent_id) query.set('agent_id', params.agent_id);
    if (params?.run_id) query.set('run_id', params.run_id);
    return requestJson<Record<string, unknown>>(`/evidence/export?${query.toString()}`, { signal });
  },

  resolveIncident: (id: string, status: 'acknowledged' | 'resolved' = 'resolved') => requestJson<{ success: boolean }>(`/incidents/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ status })
  }),

  incidentAction: (id: string, action: 'acknowledge' | 'approve' | 'reject' | 'resolve', note?: string) => requestJson<{ success: boolean; status: string; resolution: string }>(`/incidents/${encodeURIComponent(id)}/action`, {
    method: 'POST',
    body: JSON.stringify({ action, note })
  }),

  quarantineIncidentAgent: (id: string) => requestJson<{ success: boolean; agent_id: string; message: string }>(`/incidents/${encodeURIComponent(id)}/quarantine`, {
    method: 'POST',
    body: JSON.stringify({})
  }),

  dryRunPolicy: (payload: {
    action_name: string;
    target_resource?: string;
    payload?: Record<string, unknown>;
    prompt_input?: string;
    rule_expression?: string;
    category?: string;
    agent_id?: string;
  }) => requestJson<{
    matched: boolean;
    decision: 'ALLOW' | 'REVIEW' | 'BLOCK';
    risk_score: number;
    matched_rules: string[];
    explanation: string;
  }>('/policies/dry-run', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  createPolicy: (payload: {
    name: string;
    category: string;
    severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
    default_action: 'ALLOW' | 'REVIEW' | 'BLOCK';
    rule_expression: string;
    remediation_guidance: string;
  }) => requestJson<{ success: boolean; policy: Policy }>('/policies', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  deletePolicy: (id: string) => requestJson<{ success: boolean; id: string }>(`/policies/${encodeURIComponent(id)}`, {
    method: 'DELETE'
  }),

  exportCompliancePack: (signal?: AbortSignal) => requestJson<{
    compliance_standard: string;
    organization_id: string;
    exported_at: string;
    platform_version: string;
    summary: Record<string, number>;
    agent_safety_profiles: any[];
    active_guardrail_policies: any[];
    pre_release_validation_certificates: any[];
    tamper_evident_audit_log: any[];
    markdown_report: string;
    integrity_hash: string;
    verification_status: string;
  }>('/evidence/compliance-pack', { signal }),

  exportComplianceReport: (report: any, format: 'markdown' | 'json' = 'markdown') => requestJson<{
    success: boolean;
    format: string;
    filename: string;
    content: string;
  }>('/validate/compliance-report', {
    method: 'POST',
    body: JSON.stringify({ report, format })
  }),

  mcpCall: (method: string, params?: Record<string, unknown>) => requestJson<{
    jsonrpc: string;
    id: string | number | null;
    result?: Record<string, any>;
    error?: { code: number; message: string };
  }>('/mcp', {
    method: 'POST',
    headers: { Accept: 'application/json, text/event-stream' },
    body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params: params || {} })
  }),

  getTenants: () => requestJson<TenantWorkspace[]>('/tenants'),

  createTenant: (tenant: { name: string; slug?: string; environment?: string; compliance_framework?: string }) => requestJson<TenantWorkspace>('/tenants', {
    method: 'POST',
    body: JSON.stringify(tenant)
  }),

  getMembers: () => requestJson<OrganizationMember[]>('/members'),

  inviteMember: (payload: { email: string; name?: string; role: 'owner' | 'admin' | 'operator' | 'viewer' }) => requestJson<OrganizationMember & { message?: string }>('/members/invitations', {
    method: 'POST',
    body: JSON.stringify(payload)
  }),

  updateMemberRole: (memberId: string, role: 'owner' | 'admin' | 'operator' | 'viewer') => requestJson<{ success: boolean; id: string; role: OrganizationMember['role'] }>(`/members/${encodeURIComponent(memberId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ role })
  }),

  removeMember: (memberId: string) => requestJson<{ success: boolean; id: string }>(`/members/${encodeURIComponent(memberId)}`, {
    method: 'DELETE'
  }),

  revokeInvitation: (invitationId: string) => requestJson<{ success: boolean; id: string }>(`/members/invitations/${encodeURIComponent(invitationId)}`, {
    method: 'DELETE'
  }),

  switchTenant: async (tenantId: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('agenticscale_active_tenant', tenantId);
    }
    return { success: true, active_tenant_id: tenantId };
  },

  getPlatformMetrics: () => requestJson<PlatformMetrics>('/stats')
};
